import React, { useState } from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import { formatMoney } from "../../../features/utils/formatMoney";
import {
  displayRefundLineName,
  getRefundQuoteSummary,
} from "../../../utils/booking/refundQuote";
import approveRefundQuote from "../../../controllers/booking/postTicketing/refund/approveRefundQuote";
import StaffApiErrorModal, {
  getStaffApiErrorCopy,
} from "../StaffApiErrorModal";

const TYPE_LABEL = {
  ADT: "Adult",
  CHD: "Child",
  INF: "Infant",
};

function MoneyRow({ label, value, emphasize, profit, muted }) {
  return (
    <div className="flex items-baseline gap-2 py-1">
      <span
        className={`text-sm shrink-0 ${
          muted ? "text-[#64748B]" : "text-[#334155]"
        } ${emphasize ? "font-semibold text-[#0F172A]" : ""}`}
      >
        {label}
      </span>
      <span
        className="flex-1 border-b border-dotted border-[#E2E8F0] translate-y-[-3px] min-w-[8px]"
        aria-hidden
      />
      <span
        className={`text-sm tabular-nums shrink-0 ${
          profit
            ? "font-semibold text-emerald-700"
            : emphasize
              ? "font-bold text-[#0F172A] text-base"
              : "font-medium text-[#0F172A]"
        }`}
      >
        {formatMoney(value)}
      </span>
    </div>
  );
}

function BreakdownLines({ breakdown }) {
  const lines = (breakdown || []).filter(Boolean);
  if (!lines.length) {
    return (
      <p className="text-xs text-[#94A3B8]">No breakdown lines on this quote.</p>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      {lines.map((row, i) => {
        const name = displayRefundLineName(row?.name);
        const agent = Number(row?.amount || 0);
        const airline = Number(row?.airlineAmount ?? row?.amount ?? 0);
        const deducted = row?.isDeducted !== false;
        const showAirline =
          Number.isFinite(Number(row?.airlineAmount)) &&
          Math.abs(airline - agent) > 0.009;
        return (
          <div
            key={`${name}-${i}`}
            className="flex items-start justify-between gap-2 text-[13px]"
          >
            <div className="min-w-0">
              <span className="text-[#0F172A]">{name}</span>
              {deducted ? (
                <span className="ml-1.5 text-[10px] uppercase tracking-wide text-rose-600 font-semibold">
                  deducted
                </span>
              ) : (
                <span className="ml-1.5 text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">
                  return
                </span>
              )}
              {showAirline ? (
                <div className="text-[11px] text-[#94A3B8]">
                  Airline {formatMoney(airline)}
                </div>
              ) : null}
            </div>
            <span
              className={`shrink-0 tabular-nums font-medium ${
                deducted ? "text-rose-700" : "text-[#0F172A]"
              }`}
            >
              {deducted ? "−" : ""}
              {formatMoney(agent)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Confirm approveQuote for immediate refunds — staff must see
 * breakdown, agent payable, and platform profit before confirming.
 */
export default function ApproveRefundQuoteModal({ open, setOpen, data, onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  if (!open) return null;

  const summary = getRefundQuoteSummary(data || {});
  const orderId = data?.orderId || data?._id || "—";

  function close() {
    if (submitting) return;
    setOpen?.(false);
  }

  async function handleApprove() {
    if (!data?._id) return;
    setSubmitting(true);
    const res = await approveRefundQuote(data._id).finally(() =>
      setSubmitting(false),
    );
    if (res.return) {
      enqueueSnackbar(
        "Quote approved for the agent. Submit the refund on the airline next — do not credit the wallet yet.",
        { variant: "success" },
      );
      setOpen?.(false);
      onDone?.(res.data);
    } else {
      setApiError(getStaffApiErrorCopy(res, "We could not approve this quote"));
    }
  }

  return (
    <>
      <StaffApiErrorModal
        open={Boolean(apiError)}
        setOpen={(next) => {
          if (!next) setApiError(null);
        }}
        title={apiError?.title}
        text={apiError?.text}
      />
      {ReactDOM.createPortal(
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close overlay"
            className="absolute inset-0 bg-black/40"
            onClick={close}
          />
          <div className="relative w-full max-w-[560px] max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 px-6 pt-5">
              <div>
                <h2 className="text-[17px] font-semibold text-gray-900 leading-6">
                  Approve quote
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {orderId} · Immediate refund — this accepts the quote for the
                  agent. Check every number before you continue.
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                disabled={submitting}
                className="mt-0.5 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50"
              >
                <Icon icon="material-symbols:close" className="text-xl" />
              </button>
            </div>

            <div className="px-6 pb-6 pt-4 flex flex-col gap-4">
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950">
                Agent chose <span className="font-semibold">immediate refund</span>
                . Approving skips agent acceptance. Nobody else will catch a wrong
                figure — read the breakdown below.
              </div>

              {summary.rows.length ? (
                <div className="flex flex-col gap-3">
                  <h3 className="text-sm font-semibold text-[#0F172A]">
                    Quote breakdown
                  </h3>
                  {summary.rows.map((row) => (
                    <div
                      key={row.key}
                      className="rounded-xl border border-[#E8EEF5] bg-[#F8FAFC] px-3 py-3"
                    >
                      <div className="flex items-baseline justify-between gap-2 mb-2">
                        <span className="text-sm font-semibold text-[#0F172A]">
                          {TYPE_LABEL[row.travelerType] || row.travelerType}
                          <span className="ml-1.5 text-xs font-normal text-[#64748B]">
                            × {row.count}
                          </span>
                        </span>
                        <span className="text-xs text-[#64748B]">
                          Agent / pax {formatMoney(row.agentNet)}
                        </span>
                      </div>
                      <BreakdownLines breakdown={row.breakdown} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-900">
                  This quote has no passenger price breakdown. Do not approve —
                  open Enter quote and fix the figures first.
                </div>
              )}

              <div className="rounded-xl border border-[#E8EEF5] bg-white px-3 py-3 max-w-md">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-1">
                  Money summary
                </h3>
                <MoneyRow
                  label="Selected sold value"
                  value={summary.refundRunSum}
                />
                <MoneyRow
                  label="Deductible (original − Base − Tax)"
                  value={summary.totalDeductibleField || 0}
                  muted
                />
                {Number(summary.totalOpsFees || 0) > 0.0001 ? (
                  <MoneyRow
                    label="Penalty + supplier fee"
                    value={summary.totalOpsFees}
                    muted
                  />
                ) : null}
                <MoneyRow
                  label="Airline refund (Base + Tax)"
                  value={
                    summary.airlineSideTotal ?? summary.airlineRefund
                  }
                  muted
                />
                {Number(summary.totalHeldAdjustment || 0) > 0.0001 ? (
                  <MoneyRow
                    label={
                      summary.rows?.some((r) => r.markupSource === "backend")
                        ? "Intraverse Markup (from quote)"
                        : "Booking markup (estimate)"
                    }
                    value={summary.totalHeldAdjustment}
                    muted
                  />
                ) : null}
                {Number(summary.airlineRefund || 0) -
                  Number(summary.finalRefundAmount || 0) >
                0.0001 ? (
                  <MoneyRow
                    label="Quote markup kept"
                    value={
                      Number(summary.airlineRefund || 0) -
                      Number(summary.finalRefundAmount || 0)
                    }
                    muted
                  />
                ) : null}
                {Number(summary.finalRefundAmount || 0) -
                  Number(summary.airlineRefund || 0) >
                0.0001 ? (
                  <MoneyRow
                    label="Quote markdown to agent"
                    value={
                      Number(summary.finalRefundAmount || 0) -
                      Number(summary.airlineRefund || 0)
                    }
                    muted
                  />
                ) : null}
                <MoneyRow
                  label="Ticketing fee retained (not refunded)"
                  value={summary.ticketingFeeRetained}
                  muted
                />
                <MoneyRow
                  label="Refund service fee"
                  value={summary.serviceFee || 0}
                  muted
                />
                <div className="border-t border-[#E8EEF5] mt-1 pt-1">
                  <MoneyRow
                    label="Estimated payable"
                    value={summary.finalRefundAmount}
                    emphasize
                  />
                </div>
                {Number(summary.payable) > 0.0001 ? (
                  <MoneyRow
                    label="Current payable"
                    value={summary.payable}
                    muted
                  />
                ) : null}
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-2.5">
                <button
                  type="button"
                  onClick={close}
                  disabled={submitting}
                  className="h-11 flex-1 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={submitting || !summary.rows.length}
                  className="h-11 flex-1 rounded-xl bg-primary1 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
                >
                  {submitting ? (
                    <span className="buttonLoader mx-auto" />
                  ) : (
                    "Approve quote"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
